import { z } from 'zod';
import { MODALIDADE, SITUACAO_PEDIDO, FORMA_PAGAMENTO, SITUACAO_PAGAMENTO_MARCACAO, } from '../dominio/index.js';
import { diaDaSemana } from '../tempo/index.js';
import { zNomeDePessoa } from './alunos.js';
import { DURACAO_MAXIMA_MINUTOS, zData, zDiasDaSemana, zDuracaoMinutos, zHoraDaGrade, } from './agenda.js';
import { zCentavos, zId, zPaginacao, zTelefone } from './primitivos.js';
/**
 * Os contratos de reservas pedidas — a fila de pedidos de `#v-site` e
 * `#m-pedido`, os horários oferecíveis e a promoção de `#m-promo`.
 *
 * O FORMULÁRIO PÚBLICO SAIU. O documento 14 tinha uma porta sem sessão —
 * um formulário embutido no site da arena, com desafio anti-robô e
 * limite por endereço — e ela foi removida a pedido da arena, junto com
 * todo envio por WhatsApp. O pedido continua existindo e a fila continua
 * igual: o que mudou é a porta de entrada, que agora é a recepção
 * registrando o que chegou por telefone (`zPedidoDeReserva`).
 *
 * O que ficou da porta pública, e por que: a conta dos HORÁRIOS
 * OFERECÍVEIS (`FormularioPublico`, `dias`) é a mesma que a recepção quer
 * ao anotar um pedido — "sábado às 20h tem?" —, e ela já considera
 * ocupação, antecedência e a alocação fixa das turmas. Fosse descartada,
 * seria reescrita pior.
 *
 * A entrada fala a língua da recepção: DATA e HORA LOCAIS da arena, como
 * na agenda. Quem converte para instante é o servidor.
 *
 * Ver: Arena_backend/Docs/plano/14-site-reservas-publicas.md
 */
// ---------------------------------------------------------------------
// Vocabulário
// ---------------------------------------------------------------------
/**
 * O select do pedido: "Futevôlei", "Vôlei", "Locação livre". Nome
 * próprio, e não o `ROTULO_MODALIDADE` de turmas: lá "Livre" é uma
 * modalidade de turma; num pedido de horário, é alugar a quadra.
 */
export const ROTULO_MODALIDADE_NO_FORMULARIO = {
    Futvolei: 'Futevôlei',
    Volei: 'Vôlei',
    BeachTennis: 'Beach Tennis',
    Funcional: 'Funcional',
    Livre: 'Locação livre',
    Outra: 'Outra modalidade',
};
export const ROTULO_SITUACAO_PEDIDO = {
    Pendente: 'Pendente',
    Confirmado: 'Confirmado',
    Recusado: 'Recusado',
};
/**
 * "Encaixar como" de `#m-pedido`. Os dois tipos de marcação que nascem
 * de um pedido: a aula experimental — que cria também o aluno — e a
 * locação avulsa. Turma, evento e bloqueio não se pedem.
 */
export const ENCAIXES_DE_PEDIDO = ['Experimental', 'LocacaoAvulsa'];
/**
 * Os horários oferecíveis andam de hora em hora, contados da abertura:
 * "Hoje · 07h (livre)", "Hoje · 08h (livre)" — como no protótipo.
 */
export const PASSO_DO_FORMULARIO_MINUTOS = 60;
/** O motor recusa consultar mais que isso de uma vez — ver `DIAS_MAXIMOS_DE_HORARIOS_LIVRES`. */
export const DIAS_A_FRENTE_MAXIMO = 31;
/** Desconto de 100% é cortesia, não promoção — e 95% é quase sempre um zero a mais. */
export const DESCONTO_MAXIMO_PERCENTUAL = 90;
/**
 * O protótipo conta o mês como quatro semanas e supõe encher METADE das
 * horas paradas: "7 horas paradas por semana… enchendo metade delas com
 * locação a R$ 70, entram cerca de R$ 980 por mês". A conta é dele; o
 * número exato fica com o BI (documento 17).
 */
export const SEMANAS_POR_MES_NA_PROJECAO = 4;
export const FRACAO_OCUPADA_NA_PROJECAO = 0.5;
const texto = (maximo) => z.string().trim().max(maximo, `No máximo ${maximo} caracteres.`);
// ---------------------------------------------------------------------
// O pedido de reserva
// ---------------------------------------------------------------------
/**
 * O corpo de `POST /site/pedidos` — o pedido que a recepção registra.
 *
 * Ele chega por telefone, por mensagem ou no balcão: alguém liga pedindo
 * sábado às 20h e a recepção anota, para responder depois de conferir a
 * grade. É o mesmo pedido que ficava na fila vindo do formulário público
 * — a fila não mudou, mudou a porta por onde ele entra.
 *
 * Telefone OBRIGATÓRIO, e já em dígitos: "sem ele não há como confirmar
 * nem cobrar ausência". `duracaoMinutos` é opcional — sem ela, a duração
 * padrão da arena.
 */
export const zPedidoDeReserva = z.object({
    nome: zNomeDePessoa,
    telefone: zTelefone,
    modalidade: z.enum(MODALIDADE, {
        errorMap: () => ({ message: 'Escolha uma modalidade.' }),
    }),
    data: zData,
    inicio: zHoraDaGrade,
    duracaoMinutos: zDuracaoMinutos.optional(),
    mensagem: texto(500).optional(),
});
// ---------------------------------------------------------------------
// Horários oferecíveis
// ---------------------------------------------------------------------
/**
 * A modalidade que só se encaixa como LOCAÇÃO — "Locação livre". Futvôlei
 * e vôlei podem virar aula experimental; locação livre, não.
 */
export const MODALIDADE_DE_LOCACAO = 'Livre';
// ---------------------------------------------------------------------
// A recepção — configuração do formulário
// ---------------------------------------------------------------------
/**
 * O formulário público de uma arena. Nasce despublicado: slug que existe
 * sem formulário publicado responde igual a slug que não existe.
 */
export const zConfiguracaoDoFormulario = z.object({
    publicado: z.boolean(),
    modalidades: z
        .array(z.enum(MODALIDADE))
        .min(1, 'Ofereça ao menos uma modalidade.')
        .transform((lista) => MODALIDADE.filter((m) => lista.includes(m))),
    duracaoMinutos: z
        .number()
        .int()
        .min(PASSO_DO_FORMULARIO_MINUTOS, 'O horário oferecido dura ao menos uma hora.')
        .max(DURACAO_MAXIMA_MINUTOS)
        .refine((m) => m % 30 === 0, 'A duração anda de meia em meia hora.'),
    diasAFrente: z.number().int().min(1).max(DIAS_A_FRENTE_MAXIMO),
    antecedenciaMinutos: z
        .number()
        .int()
        .min(0)
        .max(48 * 60, 'No máximo 48 horas de antecedência.'),
    prazoRespostaHoras: z.number().int().min(1).max(72),
    /** Centavos por hora. Sem valor, o formulário não mostra preço. */
    precoHoraLocacao: zCentavos.nullable(),
});
/**
 * Os parâmetros de uma arena que ainda não os configurou. As duas horas
 * de prazo de resposta e os itens do select são os do protótipo.
 */
export const FORMULARIO_PADRAO = {
    publicado: false,
    modalidades: [...MODALIDADE],
    duracaoMinutos: 60,
    diasAFrente: 7,
    antecedenciaMinutos: 60,
    prazoRespostaHoras: 2,
    precoHoraLocacao: null,
};
// ---------------------------------------------------------------------
// A recepção — a fila de pedidos
// ---------------------------------------------------------------------
export const zConsultaDePedidos = zPaginacao.extend({
    situacao: z.enum(SITUACAO_PEDIDO).optional(),
    limite: z.coerce.number().int().min(1).max(100).default(25),
});
const confirmacaoComum = {
    quadraId: zId,
};
/**
 * Encaixar como AULA EXPERIMENTAL cria o aluno — e o cadastro de aluno
 * tem os obrigatórios do documento 08: nascimento e o aceite do termo.
 * O formulário público não os pede, de propósito (é pedido de horário,
 * não matrícula); quem os colhe é a recepção, na conversa de confirmação.
 */
export const zConfirmacaoExperimental = z.object({
    encaixe: z.literal('Experimental'),
    ...confirmacaoComum,
    nascimento: zData,
    responsavelFinanceiro: texto(120).optional(),
    aceiteTermo: z.literal(true, {
        errorMap: () => ({ message: 'O aceite do termo de uso e da política é obrigatório.' }),
    }),
    consentimentoImagem: z.boolean().default(false),
});
export const zConfirmacaoLocacao = z.object({
    encaixe: z.literal('LocacaoAvulsa'),
    ...confirmacaoComum,
    valor: zCentavos.optional(),
    formaPagamento: z.enum(FORMA_PAGAMENTO).optional(),
    situacaoPagamento: z.enum(SITUACAO_PAGAMENTO_MARCACAO).optional(),
    pessoas: z.number().int().min(1).max(40).optional(),
});
export const zConfirmacaoDePedido = z.discriminatedUnion('encaixe', [
    zConfirmacaoExperimental,
    zConfirmacaoLocacao,
]);
/** Recusar é registro, com motivo — "Recusado · sem quadra". */
export const zRecusaDePedido = z.object({
    motivo: z
        .string({ required_error: 'Diga por que o pedido foi recusado.' })
        .trim()
        .min(3, 'Diga por que o pedido foi recusado.')
        .max(200, 'No máximo 200 caracteres.'),
});
// ---------------------------------------------------------------------
// Promoções em faixa ociosa
// ---------------------------------------------------------------------
const faixa = {
    diasSemana: zDiasDaSemana,
    horaInicio: zHoraDaGrade,
    horaFim: zHoraDaGrade,
    descontoPercentual: z
        .number({ invalid_type_error: 'Informe o desconto em pontos percentuais.' })
        .int()
        .min(1, 'O desconto é de ao menos 1%.')
        .max(DESCONTO_MAXIMO_PERCENTUAL, `O desconto é de no máximo ${DESCONTO_MAXIMO_PERCENTUAL}%.`),
};
const MENSAGEM_FAIXA = 'O começo da faixa precisa ser antes do fim.';
export const zNovaPromocao = z
    .object({ ...faixa, validaAte: zData })
    .refine((p) => p.horaInicio < p.horaFim, { message: MENSAGEM_FAIXA, path: ['horaFim'] });
/** Tirar do formulário é `ativa: false` — a promoção fica, com a história dela. */
export const zAlteracaoDePromocao = z
    .object({
    diasSemana: faixa.diasSemana.optional(),
    horaInicio: faixa.horaInicio.optional(),
    horaFim: faixa.horaFim.optional(),
    descontoPercentual: faixa.descontoPercentual.optional(),
    validaAte: zData.optional(),
    ativa: z.boolean().optional(),
})
    .refine((a) => Object.keys(a).length > 0, { message: 'Nada para alterar.' });
export const zProjecaoDePromocao = z
    .object(faixa)
    .refine((p) => p.horaInicio < p.horaFim, { message: MENSAGEM_FAIXA, path: ['horaFim'] });
/**
 * A promoção que vale para um horário: ativa, dentro da validade, no dia
 * da semana, e com o COMEÇO do horário dentro da faixa — a locação das
 * 10h de uma faixa que acaba às 11h sai com desconto, a das 11h não.
 *
 * Duas promoções no mesmo horário não se somam: vale a de maior
 * desconto. Somar 30% e 20% daria 50% sem ninguém ter decidido 50%.
 */
export function promocaoDoHorario(promocoes, data, hora) {
    const dia = diaDaSemana(data);
    let melhor = null;
    for (const promocao of promocoes) {
        const cobre = promocao.ativa &&
            promocao.validaAte >= data &&
            promocao.diasSemana.includes(dia) &&
            promocao.horaInicio <= hora &&
            hora < promocao.horaFim;
        if (cobre && (!melhor || promocao.descontoPercentual > melhor.descontoPercentual)) {
            melhor = promocao;
        }
    }
    return melhor;
}
/** Centavos inteiros: o desconto arredonda para o centavo mais próximo. */
export function precoComDesconto(centavos, descontoPercentual) {
    return Math.round((centavos * (100 - descontoPercentual)) / 100);
}
/** O preço de uma locação de `duracaoMinutos` a `precoHora` centavos por hora. */
export function precoDaLocacao(precoHora, duracaoMinutos) {
    return Math.round((precoHora * duracaoMinutos) / 60);
}
/** A conta do protótipo — metade das horas paradas, quatro semanas por mês. */
export function receitaMensalEstimada(horasLivresPorSemana, precoHora) {
    return Math.round(horasLivresPorSemana * FRACAO_OCUPADA_NA_PROJECAO * precoHora * SEMANAS_POR_MES_NA_PROJECAO);
}
//# sourceMappingURL=site.js.map